import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { getAdvertisementStatistics } from '@/api/sponsorApi'
import { STATISTICS_DATA_FETCH_TIMEOUT } from '@/constants'
import { useMemo, useState } from 'react'
import { SectionCards, type DateFilterValues } from '../section-cards'
import { useQuery } from '@tanstack/react-query'
import {
  CalenderIcon,
  ClickIcon,
  LocationFilledIcon,
  MultipleUserIcon,
} from '../Icons'
import { formatGrowth } from '@/utils/helper'

interface ISponsorAdvertisementStatItem {
  count: number
  percentageChange?: number
  region?: string
  percentage?: string
}

type ISponsorAdvertisementStatistics = Record<
  'totalClicks' | 'averageAge' | 'audience' | 'topRegion',
  ISponsorAdvertisementStatItem
>

export function SponsorAdStatistics({
  showLabel,
}: Readonly<{ showLabel?: boolean }>) {
  const [dateFilters, setDateFilters] = useState<DateFilterValues>()

  const { data: sponsorAdStatistics, isLoading: isSponsorAdStatisticsLoading } =
    useQuery({
      queryKey: [
        SPONSOR_QUERY_KEYS.advertisementStatistics('sponsor'),
        dateFilters,
      ],
      queryFn: () => {
        return getAdvertisementStatistics({
          source: 'sponsor',
          ...(dateFilters && {
            month: dateFilters.month,
            year: dateFilters.year,
          }),
        })
      },
      refetchInterval: STATISTICS_DATA_FETCH_TIMEOUT,
      refetchIntervalInBackground: true,
      enabled: dateFilters !== undefined,
    })

  const sponsorAdCards = useMemo(() => {
    const stats: ISponsorAdvertisementStatistics = sponsorAdStatistics?.data

    return [
      {
        title: 'Total Audience',
        value: stats?.audience.count,
        icon: <MultipleUserIcon />,
        cardType: 1 as const,
        footer: 'Unique users engaged',
      },
      {
        title: 'Avg. Age',
        value: stats?.averageAge.count?.toFixed(2),
        icon: <CalenderIcon />,
        cardType: 2 as const,
        footer: 'Years old',
      },
      {
        title: 'Top Location',
        value: stats?.topRegion.region ?? 'N/A',
        icon: <LocationFilledIcon />,
        cardType: 3 as const,
        footer: `${stats?.topRegion.percentage ?? '0%'} of audience`,
      },
      {
        title: 'Total Clicks',
        value: stats?.totalClicks?.count ?? 0,
        icon: <ClickIcon />,
        cardType: 4 as const,
        growth: formatGrowth(stats?.totalClicks.percentageChange ?? 0),
      },
    ]
  }, [sponsorAdStatistics])

  return (
    <div className="flex flex-col gap-1.5">
      <SectionCards
        isLoading={isSponsorAdStatisticsLoading}
        cards={sponsorAdCards}
        showFilters
        cardLabel={showLabel ? 'Sponsor Ad Statistics' : undefined}
        filterValues={dateFilters}
        onFilterChange={val => setDateFilters(val)}
      />
    </div>
  )
}
